import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Material UI Packages. */
import {
    Box,
    Dialog,
    DialogContent,
    FormControl,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Typography,
} from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';
import LoadingButton from '@mui/lab/LoadingButton';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Dayjs packages. */
import dayjs from 'dayjs';

/** Dompurify Packages. */
import DOMPurify from 'dompurify';

/** Quill Packages. */
import Quill from 'quill';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import { guideCategory } from 'shared/config/constantsData';
import DragAndDrop from 'shared/ui/DragAndDrop/DragAndDrop';
import { PaperComponent } from 'shared/ui/DraggableDialog';
import QuillEditor from 'shared/ui/TextEditor';
import DialogHeader from 'shared/ui/DialogHeader';
import { handleDialogCloseWithDetection } from 'shared/lib/changeDetection';
import validateFields from 'shared/lib/validateFields';

const initState = () => ({
    il_category_id: 1,
    title: '',
    body: '',
    il_user_id: '',
    il_file: [], // This array stores file ids, only the file ids are permanently saved during the save process.
    modified_on: null,
    il_guideline_file: [],
});

const initValidator = {
    category: false,
    subject: false,
    content: false,
    file: false,
};

export default function GuidelineDialog(props) {
    /** Get props from Guideline.js. */
    const {
        dispatch,
        newOpen,
        setNewOpen,
        selectedRowId,
        setSelectedRowId,
        guideLineRow,
        setRowUpdateTrigger,
        setActionLoading,
    } = props;

    /** Destructuring values from useAuth0 hook. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** State for savintg all inputs in the guideline dialog. */
    const [guidelineDialogData, setGuideLineDialogData] = useState(initState());
    /** State for validating and displaying errors for required inputs. */
    const [validator, setValidator] = useState(initValidator);
    /** State for managing files that has been temporarily uploaded to an S3 folder. */
    const [tempUploadedFile, setTempUploadedFile] = useState(false);
    /** Box wrapping the content editor; set once the dialog content has mounted. */
    const [editorBox, setEditorBox] = useState(null);
    /** Editor instance the saved body has already been loaded into. */
    const bodyLoadedEditorRef = useRef(null);

    /** Load the information to be edited into the dialog by saving it in the guidelineDialogData. */
    useEffect(() => {
        if (selectedRowId) {
            const selectedRow = guideLineRow.find((row) => row.id === selectedRowId);
            if (selectedRow.il_guideline_file !== null) {
                selectedRow.il_file = selectedRow.il_guideline_file.map((file) => file.id);
            } else {
                selectedRow.il_file = [];
            }
            setGuideLineDialogData(selectedRow);
            setValidator(initValidator);
        } else {
            setGuideLineDialogData(initState());
            setValidator(initValidator);
        }
    }, [selectedRowId]);

    /**
     * Load the saved body into the editor when editing.
     * The body is the editor's own HTML shown with `white-space: pre-wrap`, so its tabs and spaces are content.
     * Quill's paste path collapses that whitespace (a Tab at the start of a line comes back as nothing),
     * so the text is kept as-is while converting the saved body.
     */
    useEffect(() => {
        if (!selectedRowId || !guidelineDialogData.body) return;
        const quill = Quill.find(editorBox?.querySelector('.ql-container'));
        if (!(quill instanceof Quill) || bodyLoadedEditorRef.current === quill) return;

        const Delta = Quill.import('delta');
        let isLoading = true;
        quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) =>
            isLoading ? new Delta().insert(node.data) : delta
        );
        const contents = quill.clipboard.convert({ html: guidelineDialogData.body, text: '' });
        isLoading = false;

        quill.setContents(contents, Quill.sources.API);
        quill.setSelection(0, Quill.sources.SILENT);
        bodyLoadedEditorRef.current = quill;
    }, [editorBox, selectedRowId, guidelineDialogData.body]);

    /** Function for reset the states when dialog is closed. */
    const resetState = () => {
        setNewOpen(false);
        setGuideLineDialogData(initState());
        setValidator(initValidator);
        setSelectedRowId(null);
    };

    /** Handler for closing dialog and detecting changes. */
    const handleClose = () => {
        handleDialogCloseWithDetection(
            selectedRowId ? guideLineRow.find((row) => row.id === selectedRowId) : null,
            guidelineDialogData,
            initState(),
            resetState
        );
    };

    /** Function to validate required fields. */
    const isInputVerified = () => {
        return validateFields(
            {
                category: guidelineDialogData.il_category_id,
                subject: guidelineDialogData.title,
                content: guidelineDialogData.body,
            },
            setValidator
        );
    };

    /** Handler for saving new or edited guide info.
     *  Before calling the API function, validation is performed.
     *  A new object is created to match the database table's key-value pairs.
     */
    const handleSaveBtn = () => {
        if (!isInputVerified()) {
            return dispatch(
                setSnackbar({
                    open: true,
                    message: 'Please complete the input fields before saving.',
                    severity: 'warning',
                })
            );
        }
        // Updates the `modified_on` value to the 'MM/DD/YY' format and sets the `last_updated` field to the current time.
        const newObject = {
            ...guidelineDialogData,
            il_user_id: user.given_name + ' ' + user.family_name,
            modified_on: dayjs(),
            id: selectedRowId ? selectedRowId : randomId(),
            email: user.email,
            body: DOMPurify.sanitize(guidelineDialogData.body),
        };
        const method = selectedRowId ? 'put' : 'post';

        callAPI({
            method: method,
            module: 'guideline',
            getAccessTokenSilently,
            email: user.email,
            obj: JSON.stringify(newObject),
            onInit: () => setActionLoading(false),
            onSuccess: (resp) => {
                dispatch(setSnackbar({ open: true, message: 'Successfully Saved.', severity: 'success' }));
                setRowUpdateTrigger((prev) => !prev);
                resetState();
            },
            onError: () =>
                dispatch(
                    setSnackbar({ open: true, message: 'Someting went wrong. Please try again.', severity: 'error' })
                ),
            onFinal: () => setActionLoading(false),
        });
    };

    /** Handler for managing inputs in the guide dialog. */
    const handleGuideInput = (event) => {
        setGuideLineDialogData((prev) => ({
            ...prev,
            [event.target.name]: event.target.value,
        }));
    };

    /** Handler for setting detail content. */
    const handleBodyInput = (html) => {
        setGuideLineDialogData((prev) => ({
            ...prev,
            body: html,
        }));
    };

    return (
        <Paper>
            <Dialog
                onClose={handleClose}
                open={newOpen}
                maxWidth={'1544px'}
                PaperProps={{ sx: { borderRadius: '18px' } }}
                PaperComponent={PaperComponent}
            >
                <DialogHeader
                    cursor
                    title={selectedRowId ? 'EDIT GUIDES & FAQs' : 'NEW GUIDES & FAQs'}
                    onClose={handleClose}
                    rightChildren={
                        <LoadingButton
                            onClick={handleSaveBtn}
                            loading={tempUploadedFile}
                            sx={{
                                color: '#00647e',
                                bgcolor: 'white',
                                width: '52px',
                                height: '30px',
                                borderRadius: '3px',
                                ':hover': { bgcolor: 'rgb(0,100,126)', color: 'white' },
                            }}
                        >
                            SAVE
                        </LoadingButton>
                    }
                />
                <DialogContent sx={{ py: 0 }}>
                    <Box sx={{ px: 2, py: 4 }}>
                        {/* Category Field */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '15px',
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    display: 'inline',
                                    color: '#003a52',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    width: '100px',
                                }}
                            >
                                Category
                            </Typography>
                            <FormControl error={validator['category']}>
                                <Select
                                    value={guidelineDialogData.il_category_id}
                                    name="il_category_id"
                                    onChange={handleGuideInput}
                                    sx={{
                                        width: '177px',
                                        fontSize: '13px',
                                        '& .MuiSelect-select': {
                                            p: 2,
                                        },
                                        height: '27px',
                                        overflow: 'hidden',
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 200,
                                            },
                                        },
                                    }}
                                >
                                    {guideCategory.map((item) => {
                                        return (
                                            <MenuItem
                                                value={item.id}
                                                key={item.id}
                                                sx={{
                                                    fontSize: '13px',
                                                }}
                                            >
                                                {item.content}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Subject Field */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '15px',
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    display: 'inline',
                                    color: '#003a52',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    width: '100px',
                                }}
                            >
                                Subject
                            </Typography>
                            <FormControl error={validator['subject']}>
                                <OutlinedInput
                                    name="title"
                                    size="small"
                                    value={guidelineDialogData.title}
                                    onChange={handleGuideInput}
                                    sx={{
                                        width: '40vw',
                                        height: '27px',
                                        '&.Mui-focused': {
                                            backgroundColor: 'white',
                                        },
                                        fontSize: '13px',
                                    }}
                                    inputProps={{ maxLength: 100 }}
                                />
                            </FormControl>
                        </Box>

                        {/* Content Field */}
                        <Box sx={{ display: 'flex', justifyContent: 'top', gap: '15px' }}>
                            <Typography
                                sx={{ color: 'primary.dark', fontSize: '13px', fontWeight: 500, width: '100px', pt: 1 }}
                            >
                                Content
                            </Typography>
                            <Box
                                ref={setEditorBox}
                                sx={{
                                    width: '40vw',
                                    height: '18rem',
                                    mb: '4rem',
                                    '& .ql-container': { fontFamily: 'NotoSans' },
                                }}
                            >
                                <QuillEditor
                                    onChange={(html) => handleBodyInput(html)}
                                    maxLength={3000}
                                />
                            </Box>
                        </Box>

                        {/* File Upload Field */}
                        <Box sx={{ display: 'flex', justifyContent: 'top', gap: '15px' }}>
                            <Typography
                                sx={{ color: 'primary.dark', fontSize: '13px', fontWeight: 500, width: '100px', pt: 1 }}
                            >
                                File Upload
                            </Typography>
                            <DragAndDrop
                                moduleName="guideline"
                                existFileList={guidelineDialogData.il_guideline_file}
                                validate={validator['file']}
                                setCalled={setGuideLineDialogData}
                                setFileUploadLoading={setTempUploadedFile}
                                fileExtensions={['pdf', 'xls', 'xlsx', 'doc', 'docx', 'png', 'jpg']}
                                maxFileSize={26214400}
                                sx={{ width: '40vw', height: '15vh' }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </Paper>
    );
}
